import { RangeValue, Tiers } from "../../api/types";
import { Plan } from "./types";

type Tier = {
  unit_amount?: number | null;
  unit_amount_decimal?: string | null;
  up_to: number | null;
};

type AddonPrice = {
  currency?: string | null;
  billing_scheme?: "tiered" | "per_unit" | string;
  amount?: number | null;
  tiers?: Tier[] | null;
};

export const getPrice = (price: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
  }).format(price / 100);
};

export const formatNumber = (num: number) => {
  return new Intl.NumberFormat("en-US").format(num);
};

export const getTierPrice = (tiers: Tiers[], value: number) => {
  const tier = tiers.find((t) => !t.up_to || value <= t.up_to);

  return tier ? parseFloat(tier.unit_amount_decimal) : 0;
};

export const getAddonPrice = (price: number, currency: string) => {
  const addonPrice = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 10,
    minimumFractionDigits: 2,
    style: "currency",
    currency: currency,
  }).format(price);

  return addonPrice;
};

export const calculateAddonPrice = (
  amount: number,
  quantity: number,
  divideBy: number = 1,
) => {
  return (amount * quantity) / divideBy;
};

export const getTotalPlanPrice = (plan: Plan, rangeValue: RangeValue) => {
  const addonsTotalPrice = Object.keys(rangeValue).reduce((acc, key) => {
    return (
      acc +
      calculateAddonPrice(
        plan?.addons?.optionalAddons?.find((addon) => addon?.key === key)
          ?.pureProductPrice || 0,
        rangeValue?.[key].quantity,
        plan?.addons?.optionalAddons?.find((addon) => addon?.key === key)
          ?.divide_by || 1,
      )
    );
  }, 0);

  return plan?.price + addonsTotalPrice;
};

export const features = {
  site_max: "Sites",
  membership_max: "Team Members",
  keyword_max: "Keywords",
  page_max: "Pages",
};
export const featuresPlus = {
  api_access: "API Access",
  crawl_interval: "Crawl Interval",
  refresh_interval: "Rank Interval",
};

export function formatSchemaPrice(
  price?: AddonPrice,
  unitLabel = "per schema",
) {
  if (!price) return null;

  const currency = (price.currency || "USD").toUpperCase();

  // Tiered case
  if (price.billing_scheme === "tiered" && price.tiers?.length) {
    const freeTier = price.tiers.find(
      (t) => Number(t.unit_amount_decimal ?? (t.unit_amount ?? 0) / 100) === 0,
    );
    const paidTier = price.tiers.find(
      (t) => Number(t.unit_amount_decimal ?? (t.unit_amount ?? 0) / 100) > 0,
    );

    if (freeTier && paidTier) {
      const paidDollars = Number(
        paidTier.unit_amount_decimal ?? (paidTier.unit_amount ?? 0) / 100,
      );

      return `${formatNumber(freeTier.up_to ?? 0)} included, then ${getAddonPrice(paidDollars, currency)} ${unitLabel}`;
    }

    // a single level or there is no free/paid division
    const t = price.tiers[0];
    const dollars = Number(t.unit_amount_decimal ?? (t.unit_amount ?? 0) / 100);

    return `${getAddonPrice(dollars, currency)} ${unitLabel}`;
  }

  // Non-tiered (amount in cents)
  const cents = price.amount ?? 0;
  const dollars = cents / 100;

  return `${getAddonPrice(dollars, currency)} ${unitLabel}`;
}
